import styled from "@emotion/styled";

import { RelevanceScoreEnum } from "lib/generated/saved-views/viewConfiguration";
import { LeadDetails } from "../../../lib/hooks/api/leads/useLead";
import { useLeadEntityActions } from "../../../lib/hooks/api/leads/useLeadEntityActions";
import { DocumentCategory } from "../../../lib/types/graphQLEnums";
import { Flex } from "../../../styles/utility-components";
import { generateContactsUrl } from "../../contacts/utils";
import {
  DEFAULT_DOCUMENT_FILTERS,
  documentsUrl,
  simplifiedDocumentTypes,
} from "../../documents/utils";
import EntitySavedRow from "../../entities/save_components/EntitySavedRow";
import { SaveContactEntitiesCard } from "../../entities/save_components/SaveContactEntitiesCard";
import SaveDocumentEntitiesCard from "../../entities/save_components/SaveDocumentEntitiesCard";
import { SaveNoticeEntitiesCard } from "../../entities/save_components/SaveNoticeEntitiesCard";
import { AllEntities } from "../../entities/utils";
import { generateNoticeSearchUrl } from "../../notices/utils";

const getEntitySearchUrls = (lead: LeadDetails): Record<string, string> => {
  const buyerIds = lead.buyers?.map((buyer) => buyer.id) ?? [];
  const relevantDocumentCategories = simplifiedDocumentTypes
    .filter((type) => type.value !== DocumentCategory.Other)
    .map((type) => type.value);

  const noticesSearchUrl = generateNoticeSearchUrl({
    relevanceScore: [RelevanceScoreEnum.Low, RelevanceScoreEnum.Medium, RelevanceScoreEnum.High],
    buyers: buyerIds,
  });

  const contactsSearchUrl = generateContactsUrl({ buyerGuids: buyerIds });

  const documentsSearchUrl = documentsUrl({
    ...DEFAULT_DOCUMENT_FILTERS,
    organisationIds: buyerIds,
    categories: relevantDocumentCategories,
  });

  return {
    noticesSearchUrl,
    contactsSearchUrl,
    documentsSearchUrl,
  };
};

export default function LeadAttachments({ lead }: { lead: LeadDetails }) {
  const { noticesSearchUrl, contactsSearchUrl, documentsSearchUrl } = getEntitySearchUrls(lead);

  const { removeLeadEntities } = useLeadEntityActions({ leadId: lead.id });

  const removeEntity = ({
    entityId,
    entityType,
  }: {
    entityId: string;
    entityType: AllEntities;
  }) => {
    removeLeadEntities([
      {
        entityType,
        entityId,
      },
    ]);
  };

  return (
    <Container aria-label="Lead attachments">
      <EntitySavedRow entity="notice" entityCount={lead.notices.length}>
        <SaveNoticeEntitiesCard
          notices={lead.notices.map((n) => {
            return {
              ...n,
              name: n.name ?? "Untitled Notice",
              stagev2: n.stagev2 ?? null,
              expiryDate: n.expiryDate ?? null,
              closeDate: n.closeDate ?? null,
              publishDate: n.publishDate ?? null,
              procurementStageId: n.procurementStage.id,
            };
          })}
          entityUrl={noticesSearchUrl}
          removeEntity={removeEntity}
          contextSource="Lead details page"
        />
      </EntitySavedRow>

      <EntitySavedRow entity="contact" entityCount={lead.contacts.length}>
        <SaveContactEntitiesCard
          contacts={lead.contacts}
          entityUrl={contactsSearchUrl}
          removeEntity={removeEntity}
        />
      </EntitySavedRow>
      <EntitySavedRow entity="document" entityCount={lead.documents.length}>
        <SaveDocumentEntitiesCard
          documents={lead.documents.map((doc) => {
            return {
              ...doc,
              title: doc.title ?? "Untitled Document",
              publishedAt: doc.publishedAt ?? null,
              category: doc.category ?? "",
              buyers: doc.buyers ?? [],
            };
          })}
          entityUrl={documentsSearchUrl}
          removeEntity={removeEntity}
        />
      </EntitySavedRow>
    </Container>
  );
}

const Container = styled(Flex)({
  flex: "1 1 0",
  flexDirection: "column",
  gap: 0,
  overflowY: "auto",
  height: "100%",
  minHeight: 0,
  position: "relative",
  "& > *": {
    flexShrink: 0,
  },
});
